import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Flex, Spin, Tabs, Tag, type TagProps } from 'antd';
import type { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Entry {
  key: string;
  url?: string;
  component: React.ReactNode;
  label: React.ReactNode;
  disabled?: boolean;
  count?: number;
  tag?: {
    color?: TagProps['color'];
    label: string;
  };
}

interface Props {
  entries: Array<Entry>;
  defaultEntryKey?: string;
  isLoading?: boolean;
}

export const TabsNavigation: FC<Props> = ({ entries, defaultEntryKey, isLoading = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const computedDefaultKey = defaultEntryKey ?? entries[0]?.key;

  const segments = location.pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];

  const hasEntryKey = entries.some((e) => e.key === lastSegment);
  const activeKey = hasEntryKey ? lastSegment : computedDefaultKey;

  const items = entries.map((e) => ({
    key: e.key,
    label: (
      <Flex gap={8}>
        <div>{e.label}</div>
        {e.tag && <Tag color={e.tag.color}>{e.tag.label.toUpperCase()}</Tag>}
        {e.count != null && <CounterLabel>{numberFormatter.format(e.count)}</CounterLabel>}
      </Flex>
    ),
    disabled: e.disabled,
    children: e.component,
  }));

  if (isLoading) {
    items.push({
      key: 'loading',
      label: (
        <div>
          <Spin size="small" /> Loading...
        </div>
      ),
      disabled: true,
      children: undefined,
    });
  }

  return (
    <StyledTabs
      defaultActiveKey={computedDefaultKey}
      activeKey={activeKey}
      onChange={(key) => {
        const target = entries.find((e) => e.key === key);
        if (target) {
          navigate({ pathname: target.url ?? target.key, search: location.search });
        }
      }}
      type="card"
      items={items}
    />
  );
};

const numberFormatter = Intl.NumberFormat('en', { notation: 'compact' });

const StyledTabs = styled(Tabs)`
  height: 100%;

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }

  .ant-tabs-nav {
    margin-bottom: 0rem;     
  }

  .ant-tabs-tab {
    background-color: white !important;
    border-color: ${RacemapColors.LightGray} !important;
  }
`;

const CounterLabel = styled.div`
  font-weight: 600;
`;
